// @ts-strict-ignore
import { useFloating, arrow, offset, shift, type Placement } from "@floating-ui/react-dom";
import { $isLinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $isAtNodeEnd } from "@lexical/selection";
import { mergeRegister } from "@lexical/utils";
import { $getSelection, $isRangeSelection } from "lexical";
import { useRef, useEffect, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@aviary/components/Button";
import { l } from "@aviary/locales/i18n";

import * as styles from "./LinkInput.styles";
interface Props {
  isLinkSelected: boolean;
  placement?: Placement;
}

const LinkInput = ({ isLinkSelected, placement = "bottom-start" }: Props) => {
  const { t } = useTranslation();

  const [editor] = useLexicalComposerContext();

  const arrowRef = useRef(null);

  const [isOpen, setIsOpen] = useState<boolean>(isLinkSelected);
  const {
    floating,
    reference,
    x,
    y,
    middlewareData: { arrow: { x: arrowX, y: arrowY } = {} },
    refs,
  } = useFloating({
    placement,
    middleware: [arrow({ element: arrowRef }), offset(10), shift({ padding: 8 })],
  });
  const linkRef = useRef(null);

  const [linkUrl, setLinkUrl] = useState("");

  const positionTooltip = useCallback(() => {
    reference(getBoundingLinkRect());
  }, [reference]);

  // ensure input displays the correct URL
  const updateLinkInput = useCallback(() => {
    const selection = $getSelection();

    positionTooltip();

    if ($isRangeSelection(selection)) {
      const node = getSelectedNode(selection);
      const parent = node?.getParent();
      if ($isLinkNode(parent)) {
        setLinkUrl(parent.getURL());
      } else if ($isLinkNode(node)) {
        setLinkUrl(node.getURL());
      } else {
        setLinkUrl("");
      }
    }
  }, [positionTooltip]);

  const getBoundingLinkRect = () => {
    const currentSelection = window.getSelection();

    if (!linkRef.current && currentSelection.rangeCount > 0) {
      const virtualElement = currentSelection.getRangeAt(0);

      return virtualElement;
    }
  };

  useEffect(() => {
    setIsOpen(isLinkSelected);

    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateLinkInput();
        });
      })
    );
  }, [editor, updateLinkInput, isLinkSelected]);

  // allows for cursor to bookend link
  useEffect(() => {
    editor.getEditorState().read(() => {
      updateLinkInput();
    });
  }, [editor, updateLinkInput]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      const floatingEl = refs.floating.current;
      const target = event.target as Node | null;

      if (!target || floatingEl?.contains(target)) return;

      // Unwrap the link if the user dismissed without entering a real URL.
      // LinkButton inserts a placeholder "https://" link on open, so a still-default
      // URL means no actual link was applied.
      if (!linkUrl || linkUrl === "https://") {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
      }

      setIsOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, editor, refs.floating, linkUrl]);

  const getSelectedNode = selection => {
    const anchor = selection?.anchor;
    const focus = selection?.focus;
    const anchorNode = anchor?.getNode();
    const focusNode = focus?.getNode();
    if (anchorNode === focusNode) {
      return anchorNode;
    }
    const isBackward = selection.isBackward();

    // selection with mouse from right to left
    if (isBackward) {
      return $isAtNodeEnd(focus) ? anchorNode : focusNode;
    } else {
      return $isAtNodeEnd(anchor) ? focusNode : anchorNode;
    }
  };

  if (!isOpen) {
    return null;
  }

  const handleApplyButtonClicked = () => {
    // LinkButton inserts a placeholder "https://" link on open. If the user clicks
    // Apply without entering a real URL, unwrap the link instead of applying.
    if (!linkUrl || linkUrl === "https://") {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    } else {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, {
        url: linkUrl,
        target: "_blank",
        rel: "noopener noreferrer",
      });
    }
    setIsOpen(false);
  };

  return (
    <div
      css={styles.popperWrapper}
      ref={floating}
      style={{
        top: y ?? 0,
        left: x ?? 0,
      }}
    >
      <div
        css={styles.arrow}
        ref={arrowRef}
        style={{
          top: arrowY ?? 0,
          left: arrowX ?? 0,
        }}
      />

      <div css={styles.linkWrapper}>
        <input
          aria-label={t(l.aviary.richTextEditor.highlightedLink)}
          id="link"
          type="text"
          value={linkUrl}
          onChange={e => {
            setLinkUrl(e.target.value);
          }}
          onBlur={handleApplyButtonClicked}
          css={styles.input}
        />
        <Button intention="secondary" onClick={handleApplyButtonClicked}>
          {t(l.aviary.richTextEditor.apply)}
        </Button>
      </div>
    </div>
  );
};

export { LinkInput };
